import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import PartnerApplicationFilters from '../../components/partner/PartnerApplicationFilters';
import PartnerApplicationList from '../../components/partner/PartnerApplicationList';
import partnerService from '../../services/partnerService';
import showToast from '../../utils/toast';
import '../../components/partner/partner-styles.css';

/**
 * Main Partner Dashboard Page
 * Displays applications for Lead Channel Partner users
 */
const PartnerDashboard = ({ addNotification }) => {
    const user = useSelector((state) => state.auth.user);
    const partnerId = user?.id;
    // Extract Partner Model (CPL vs CPD) from user config
    const partnerModel = user?.userConfig?.partnerModel;
    const navigate = useNavigate();

    // State Management
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        from: 0,
        to: 0
    });

    const [filters, setFilters] = useState({});

    // Load initial data
    useEffect(() => {
        loadDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDashboardData = () => {
        loadApplications();
    };

    const loadApplications = async (page = pagination.currentPage, filterParams = filters) => {
        setLoading(true);
        try {
            const response = await partnerService.getApplications({
                page,
                limit: pagination.limit,
                ...filterParams
            });

            const payload = response.data || response;
            const list = payload.applications || (payload.data && payload.data.applications) || [];
            const meta = payload.pagination || (payload.data && payload.data.pagination) || {
                total: list.length,
                page: page,
                limit: pagination.limit,
                totalPages: 1
            };

            setApplications(list);
            setPagination({
                currentPage: meta.page,
                limit: meta.limit,
                total: meta.total,
                totalPages: meta.totalPages,
                from: (meta.page - 1) * meta.limit + 1,
                to: Math.min(meta.page * meta.limit, meta.total)
            });
        } catch (error) {
            console.error('Error loading applications:', error);

            // Check for token expiration
            if (handleTokenExpiration(error)) {
                return;
            }

            showToast('Failed to load applications', 'error');
            setApplications([]);
            setPagination({
                currentPage: 1,
                limit: 20,
                total: 0,
                totalPages: 0,
                from: 0,
                to: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper function to check for token expiration
    const handleTokenExpiration = (error) => {
        if (error && error.name === "TokenExpiredError" ||
            (error.message && error.message.includes("jwt expired")) ||
            (typeof error === 'object' && error.message && error.message.name === "TokenExpiredError")) {
            showToast("Session expired. Please login again.", 'warning');
            addNotification("Session expired. Please login again.", 'warning');
            // Clear session storage
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("userInfo");
            // Redirect to login
            setTimeout(() => {
                navigate("/");
            }, 2000);
            return true;
        }
        return false;
    };

    // Event Handlers
    const handleFilterChange = (filterParams) => {
        setFilters(filterParams);
        loadApplications(1, filterParams);
    };

    const handlePageChange = (page) => {
        loadApplications(page, filters);
    };

    const handleLimitChange = (limit) => {
        setPagination({ ...pagination, limit });
        loadApplications(1, filters);
    };

    const handleViewApplicationDetails = (applicationId) => {
        // Navigate to application details page or show modal
        console.log('View application details:', applicationId);
        showToast(`Viewing details for application ${applicationId}`, 'info');
    };

    const handleDownloadCSV = () => {
        if (!applications || applications.length === 0) {
            showToast('No data to export', 'warning');
            return;
        }

        // Prepare CSV data
        // Prepare CSV data
        let headers = ['Created Date', 'Application ID', 'Mobile Number'];

        if (partnerModel === 'CPL') {
            headers.push('OTP Verified');
        } else {
            // Default CPD columns
            headers.push('Current Status', 'Disbursed Amount', 'Disbursed Date');
        }

        const csvRows = [headers.join(',')];

        applications.forEach(app => {
            let row = [
                app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-IN') : '',
                app.applicationId || app.id || '',
                app.mobile || ''
            ];

            if (partnerModel === 'CPL') {
                // CPL specific columns
                row.push(app.otpVerified ? 'Yes' : 'No');
            } else {
                // CPD specific columns
                row.push(app.leadStatus || '');
                row.push(app.disbursalAmount ? app.disbursalAmount : '-');
                row.push(app.disbursalDate ? new Date(app.disbursalDate).toLocaleDateString('en-IN') : '-');
            }

            csvRows.push(row.join(','));
        });

        // Create and download CSV file
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `partner_applications_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast('CSV downloaded successfully', 'success');
    };

    return (
        <div id="wrapper">
            <div className="normalheader">
                <div className="hpanel">
                    <div className="panel-body">
                        <h2 className="font-light m-b-xs">
                            Partner Dashboard
                        </h2>
                        <p style={{ color: '#7c7c7c', fontSize: '14px' }}>
                            Welcome back, {user?.name || 'Partner'} - Track your lead applications and performance
                        </p>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="hpanel">
                            <div className="panel-body">
                                <PartnerApplicationFilters
                                    onFilterChange={handleFilterChange}
                                    onDownloadCSV={handleDownloadCSV}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-12">
                        <div className="hpanel">
                            <div className="panel-heading">
                                Applications
                            </div>
                            <div className="panel-body">
                                <PartnerApplicationList
                                    applications={applications}
                                    loading={loading}
                                    pagination={pagination}
                                    onPageChange={handlePageChange}
                                    onLimitChange={handleLimitChange}
                                    onViewDetails={handleViewApplicationDetails}
                                    partnerModel={partnerModel}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default PartnerDashboard;
